import { useCallback, useMemo, useState } from 'react';
import { Copy, Eye, Loader2, Plus, RefreshCw, ShieldOff } from 'lucide-react';
import {
  PortalDsBadge,
  PortalDsButton,
  PortalDsCard,
  PortalDsField,
  PortalDsInput,
  PortalDsSectionHeader,
  portalDs,
} from 'src/components/portal-design-system';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { DataTable, type DataTableColumn } from 'src/components/shared';
import { cn } from 'src/lib/utils';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { useCopy } from 'src/hooks/useCopy';
import { getReportingApiV1BaseUrl } from 'src/redux/admin/reporting-api-v1/reporting-api-v1.base';
import {
  ReportingApiKey,
  ReportingApiKeyScope,
  useCreateReportingApiKeyMutation,
  useGetReportingApiKeysQuery,
  useRevealReportingApiKeyMutation,
  useRevokeReportingApiKeyMutation,
  useRotateReportingApiKeyMutation,
} from 'src/pages/operations/endpoints/reporting-api-keys.endpoints';

type KeySecretDialogState = {
  title: string;
  description: string;
  keyValue: string;
  clientTitle: string;
  scope?: ReportingApiKeyScope;
};

function sortReportingApiKeys(keys: ReportingApiKey[]): ReportingApiKey[] {
  return [...keys].sort((left, right) => {
    if (left.is_never_used !== right.is_never_used) {
      return left.is_never_used ? -1 : 1;
    }
    return new Date(right.created_at).getTime() - new Date(left.created_at).getTime();
  });
}

interface ManageClientApiKeysCardProps {
  extClientId: string;
  clientTitle?: string;
}

export function ManageClientApiKeysCard({
  extClientId,
  clientTitle,
}: ManageClientApiKeysCardProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [pendingRevoke, setPendingRevoke] = useState<ReportingApiKey | null>(null);
  const [pendingRotate, setPendingRotate] = useState<ReportingApiKey | null>(null);
  const [secretDialog, setSecretDialog] = useState<KeySecretDialogState | null>(null);

  const { toClipboard } = useCopy();
  const { data: keys = [], isLoading, isFetching, refetch } = useGetReportingApiKeysQuery(extClientId);
  const [createKey, createState] = useCreateReportingApiKeyMutation();
  const [revealKey, revealState] = useRevealReportingApiKeyMutation();
  const [revokeKey, revokeState] = useRevokeReportingApiKeyMutation();
  const [rotateKey, rotateState] = useRotateReportingApiKeyMutation();

  const sortedKeys = useMemo(() => sortReportingApiKeys(keys), [keys]);
  const apiDocsUrl = useMemo(() => `${getReportingApiV1BaseUrl()}/docs`, []);

  const openSecretDialog = (state: KeySecretDialogState) => {
    setSecretDialog(state);
  };

  const handleCreate = async() => {
    if (!createName.trim()) {
      return;
    }

    try {
      const result = await createKey({
        ext_client_id: extClientId,
        name: createName.trim(),
      }).unwrap();
      setCreateOpen(false);
      setCreateName('');
      createState.reset();
      openSecretDialog({
        title: 'API key created',
        description: 'Copy this key now. It will not be shown again unless you reveal it from the key list.',
        keyValue: result.key,
        clientTitle: result.client_title,
        scope: result.scope,
      });
    } catch {
      // RTK global error handling surfaces API failures.
    }
  };

  const handleReveal = useCallback(async(key: ReportingApiKey) => {
    try {
      const result = await revealKey(key.id).unwrap();
      revealState.reset();
      openSecretDialog({
        title: 'API key revealed',
        description: `Full key for "${key.name}". Transmit securely and close when finished.`,
        keyValue: result.key,
        clientTitle: key.client_title,
      });
    } catch {
      // RTK global error handling surfaces API failures.
    }
  }, [revealKey, revealState]);

  const handleCopyKey = useCallback(async(key: ReportingApiKey) => {
    try {
      const result = await revealKey(key.id).unwrap();
      revealState.reset();
      await toClipboard(result.key, 'API key copied');
    } catch {
      // RTK global error handling surfaces API failures.
    }
  }, [revealKey, revealState, toClipboard]);

  const handleConfirmRotate = async() => {
    if (!pendingRotate) {
      return;
    }

    try {
      const result = await rotateKey(pendingRotate.id).unwrap();
      setPendingRotate(null);
      rotateState.reset();
      openSecretDialog({
        title: 'API key rotated',
        description: `The previous key for "${pendingRotate.name}" is revoked. Copy the replacement key now.`,
        keyValue: result.key,
        clientTitle: result.client_title,
        scope: result.scope,
      });
    } catch {
      // RTK global error handling surfaces API failures.
    }
  };

  const handleConfirmRevoke = async() => {
    if (!pendingRevoke) {
      return;
    }

    try {
      await revokeKey(pendingRevoke.id).unwrap();
      setPendingRevoke(null);
    } catch {
      // RTK global error handling surfaces API failures.
    }
  };

  const columns = useMemo<DataTableColumn<ReportingApiKey>[]>(() => [
    {
      id: 'name',
      header: 'Name',
      accessor: (row) => row.name,
      sortable: true,
    },
    {
      id: 'masked_key',
      header: 'Key',
      accessor: (row) => row.masked_key,
      render: (row) => (
        <span className={portalDs.tableCellMono}>
          {row.masked_key}
        </span>
      ),
    },
    {
      id: 'created_at',
      header: 'Created',
      accessor: (row) => row.created_at,
      sortable: true,
      render: (row) => formatDisplayDateTime(row.created_at),
    },
    {
      id: 'last_used_at',
      header: 'Last used',
      accessor: (row) => row.last_used_at,
      sortable: true,
      render: (row) => (
        row.is_never_used ? (
          <span className="font-medium text-[#B45309]">
            Never used
          </span>
        ) : (
          formatDisplayDateTime(row.last_used_at ?? undefined)
        )
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (row) => row.status,
      render: (row) => (
        <PortalDsBadge
          variant={row.status === 'active' ? 'green' : 'gray'}
          pill
        >
          {row.status}
        </PortalDsBadge>
      ),
    },
    {
      id: 'actions',
      header: '',
      accessor: () => '',
      render: (row) => (
        <div className="flex justify-end gap-2">
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            disabled={row.status === 'revoked' || revealState.isLoading}
            onClick={(event) => {
              event.stopPropagation();
              void handleReveal(row);
            }}
          >
            <Eye
              className="size-3.5"
              aria-hidden
            />
            Show
          </PortalDsButton>
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            disabled={row.status === 'revoked' || revealState.isLoading}
            onClick={(event) => {
              event.stopPropagation();
              void handleCopyKey(row);
            }}
          >
            <Copy
              className="size-3.5"
              aria-hidden
            />
            Copy
          </PortalDsButton>
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            disabled={row.status === 'revoked' || rotateState.isLoading}
            onClick={(event) => {
              event.stopPropagation();
              setPendingRotate(row);
            }}
          >
            <RefreshCw
              className="size-3.5"
              aria-hidden
            />
            Rotate
          </PortalDsButton>
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            disabled={row.status === 'revoked' || revokeState.isLoading}
            className="border-[#FECACA] text-[#DC2626] hover:border-[#FECACA] hover:bg-[#FEF2F2]"
            onClick={(event) => {
              event.stopPropagation();
              setPendingRevoke(row);
            }}
          >
            <ShieldOff
              className="size-3.5"
              aria-hidden
            />
            Revoke
          </PortalDsButton>
        </div>
      ),
    },
  ], [
    handleCopyKey,
    handleReveal,
    revealState.isLoading,
    revokeState.isLoading,
    rotateState.isLoading,
  ]);

  return (
    <>
      <PortalDsCard>
        <PortalDsSectionHeader
          title="API Key"
          titleId="manage-client-api-keys-heading"
          description={
            clientTitle
              ? `Keys scoped to ${clientTitle}. Access matches this client's reporting entitlements and account scope.`
              : "Keys scoped to this client. Access matches this client's reporting entitlements and account scope."
          }
          actions={(
            <div className="flex flex-wrap items-center gap-2">
              <PortalDsButton
                type="button"
                variant="secondary"
                size="sm"
                disabled={isFetching}
                onClick={() => void refetch()}
              >
                <RefreshCw
                  className={cn('size-3.5', isFetching && 'animate-spin')}
                  aria-hidden
                />
                Refresh
              </PortalDsButton>
              <PortalDsButton
                type="button"
                variant="primary"
                size="sm"
                onClick={() => setCreateOpen(true)}
              >
                <Plus
                  className="size-3.5"
                  aria-hidden
                />
                Create key
              </PortalDsButton>
            </div>
          )}
        />

        <div>
          {isLoading ? (
            <div className="flex items-center gap-2 text-[#5E6278]">
              <Loader2
                className="size-4 animate-spin"
                aria-hidden
              />
              <span className={portalDs.bodySm}>
                Loading API keys…
              </span>
            </div>
          ) : (
            <DataTable
              rows={sortedKeys}
              columns={columns}
              getRowKey={(row) => row.id}
              emptyMessage="No API keys yet. Create a key to grant Reporting API access."
              defaultSort={null}
              pageSize={10}
            />
          )}
        </div>

        <div className="mt-5 rounded-xl border border-[#EDEEF2] bg-[#F7F8FA] p-4">
          <p className={cn(portalDs.label, 'mb-1')}>
            API documentation
          </p>
          <p className={cn(portalDs.bodySm, 'mb-3')}>
            Interactive docs for the Reporting and Surveys APIs (auth, endpoints, and dataset catalogue).
          </p>
          <div className="flex flex-wrap items-start gap-2">
            <a
              href={apiDocsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                portalDs.link,
                'min-w-0 flex-1 break-all rounded-lg border border-[#EDEEF2] bg-white px-3 py-2 font-mono text-[13px] no-underline hover:underline',
              )}
            >
              {apiDocsUrl}
            </a>
            <PortalDsButton
              type="button"
              variant="secondary"
              size="sm"
              className="shrink-0"
              onClick={() => void toClipboard(apiDocsUrl, 'Documentation link copied')}
            >
              <Copy
                className="size-3.5"
                aria-hidden
              />
              Copy
            </PortalDsButton>
          </div>
        </div>
      </PortalDsCard>

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) {
            setCreateName('');
          }
        }}
      >
        <DialogContent className="rounded-xl border border-[#EDEEF2] bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold tracking-[-0.02em] text-[#0D0F1A]">
              Create API key
            </DialogTitle>
            <DialogDescription className="text-[14px] text-[#5E6278]">
              Name the key for
              {' '}
              {clientTitle ?? 'this client'}
              . The full key is shown once after creation.
            </DialogDescription>
          </DialogHeader>

          <PortalDsField
            label="Name"
            htmlFor="manage-client-create-key-name"
          >
            <PortalDsInput
              id="manage-client-create-key-name"
              value={createName}
              placeholder="e.g. Production dashboard"
              required
              onChange={(event) => setCreateName(event.target.value)}
            />
          </PortalDsField>

          <DialogFooter className="gap-2 sm:gap-0">
            <PortalDsButton
              type="button"
              variant="secondary"
              size="md"
              onClick={() => setCreateOpen(false)}
            >
              Cancel
            </PortalDsButton>
            <PortalDsButton
              type="button"
              variant="primary"
              size="md"
              disabled={!createName.trim() || createState.isLoading}
              onClick={() => void handleCreate()}
            >
              {createState.isLoading ? 'Creating…' : 'Create key'}
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={secretDialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSecretDialog(null);
          }
        }}
      >
        <DialogContent className="rounded-xl border border-[#EDEEF2] bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold tracking-[-0.02em] text-[#0D0F1A]">
              {secretDialog?.title}
            </DialogTitle>
            <DialogDescription className="text-[14px] text-[#5E6278]">
              {secretDialog?.description}
            </DialogDescription>
          </DialogHeader>

          {secretDialog ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-[#EDEEF2] bg-[#F7F8FA] p-4">
                <p className={cn(portalDs.bodySm, 'mb-2 text-[#5E6278]')}>
                  Client:
                  {' '}
                  {secretDialog.clientTitle}
                </p>
                <div className="flex items-start gap-2">
                  <code className="min-w-0 flex-1 break-all rounded-lg bg-white px-3 py-2 text-[13px] text-[#0D0F1A]">
                    {secretDialog.keyValue}
                  </code>
                  <PortalDsButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="shrink-0"
                    onClick={() => void toClipboard(secretDialog.keyValue, 'API key copied')}
                  >
                    <Copy
                      className="size-3.5"
                      aria-hidden
                    />
                    Copy
                  </PortalDsButton>
                </div>
              </div>

              {secretDialog.scope ? (
                <div className="rounded-xl border border-[#EDEEF2] px-4 py-3">
                  <p className={cn(portalDs.bodySm, 'font-medium text-[#0D0F1A]')}>
                    Resolved scope
                  </p>
                  <p className={cn(portalDs.bodySm, 'mt-1 text-[#5E6278]')}>
                    {secretDialog.scope.sources_count}
                    {' '}
                    sources,
                    {' '}
                    {secretDialog.scope.accounts_count}
                    {' '}
                    accounts,
                    {' '}
                    {secretDialog.scope.campaigns_count}
                    {' '}
                    campaigns
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}

          <DialogFooter>
            <PortalDsButton
              type="button"
              variant="primary"
              size="md"
              onClick={() => setSecretDialog(null)}
            >
              Done
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingRevoke !== null}
        onOpenChange={(open) => !open && setPendingRevoke(null)}
      >
        <DialogContent className="rounded-xl border border-[#EDEEF2] bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold tracking-[-0.02em] text-[#0D0F1A]">
              Revoke API key?
            </DialogTitle>
            <DialogDescription className="text-[14px] text-[#5E6278]">
              {pendingRevoke
                ? `Revoke "${pendingRevoke.name}"? The key stops working on the next request and remains listed for usage history.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <PortalDsButton
              type="button"
              variant="secondary"
              size="md"
              onClick={() => setPendingRevoke(null)}
            >
              Cancel
            </PortalDsButton>
            <PortalDsButton
              type="button"
              variant="secondary"
              size="md"
              disabled={revokeState.isLoading}
              className="border-[#FECACA] text-[#DC2626] hover:border-[#FECACA] hover:bg-[#FEF2F2]"
              onClick={() => void handleConfirmRevoke()}
            >
              {revokeState.isLoading ? 'Revoking…' : 'Revoke key'}
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingRotate !== null}
        onOpenChange={(open) => !open && setPendingRotate(null)}
      >
        <DialogContent className="rounded-xl border border-[#EDEEF2] bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold tracking-[-0.02em] text-[#0D0F1A]">
              Rotate API key?
            </DialogTitle>
            <DialogDescription className="text-[14px] text-[#5E6278]">
              {pendingRotate
                ? `Issue a replacement for "${pendingRotate.name}" and revoke the current key immediately.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <PortalDsButton
              type="button"
              variant="secondary"
              size="md"
              onClick={() => setPendingRotate(null)}
            >
              Cancel
            </PortalDsButton>
            <PortalDsButton
              type="button"
              variant="primary"
              size="md"
              disabled={rotateState.isLoading}
              onClick={() => void handleConfirmRotate()}
            >
              {rotateState.isLoading ? 'Rotating…' : 'Rotate key'}
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ManageClientApiKeysCard;
